/**
 * Time utility functions for chat messages
 */

/**
 * Format timestamp to relative time (e.g., "Just now", "5 min ago", "Yesterday")
 */
export const formatRelativeTime = (timestamp: string): string => {
  try {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - messageTime.getTime()) / 1000);

    // Just now (< 1 minute)
    if (diffInSeconds < 60) {
      return 'Just now';
    }

    // Minutes ago (< 1 hour)
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
    }

    // Hours ago (< 24 hours)
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }

    // Yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (
      messageTime.getDate() === yesterday.getDate() &&
      messageTime.getMonth() === yesterday.getMonth() &&
      messageTime.getFullYear() === yesterday.getFullYear()
    ) {
      return 'Yesterday';
    }

    // This week (show day name)
    if (diffInSeconds < 604800) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[messageTime.getDay()];
    }

    // Older (show date)
    const month = (messageTime.getMonth() + 1).toString().padStart(2, '0');
    const day = messageTime.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${messageTime.getFullYear()}`;
  } catch (error) {
    return '';
  }
};

/**
 * Format timestamp to time (HH:MM)
 */
export const formatTime = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch (error) {
    return '';
  }
};

/**
 * Check if two timestamps are on the same day
 */
export const isSameDay = (timestamp1: string, timestamp2: string): boolean => {
  try {
    const date1 = new Date(timestamp1);
    const date2 = new Date(timestamp2);
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  } catch (error) {
    return false;
  }
};

/**
 * Get date separator label (e.g., "Today", "Yesterday", "January 15, 2024")
 */
export const getDateSeparatorLabel = (timestamp: string): string => {
  try {
    const now = new Date();
    const messageDate = new Date(timestamp);

    // Today
    if (
      messageDate.getDate() === now.getDate() &&
      messageDate.getMonth() === now.getMonth() &&
      messageDate.getFullYear() === now.getFullYear()
    ) {
      return 'Today';
    }

    // Yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (
      messageDate.getDate() === yesterday.getDate() &&
      messageDate.getMonth() === yesterday.getMonth() &&
      messageDate.getFullYear() === yesterday.getFullYear()
    ) {
      return 'Yesterday';
    }

    // Other dates
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const month = months[messageDate.getMonth()];
    const day = messageDate.getDate();
    const year = messageDate.getFullYear();

    return `${month} ${day}, ${year}`;
  } catch (error) {
    return '';
  }
};

/**
 * Check if two messages should be grouped together
 * Messages are grouped if they're from the same sender and sent within 5 minutes
 */
export const shouldGroupMessages = (
  prevMessage: { sender_id: string; timestamp: string } | null,
  currentMessage: { sender_id: string; timestamp: string }
): boolean => {
  if (!prevMessage) return false;

  // Different senders
  if (prevMessage.sender_id !== currentMessage.sender_id) return false;

  // Check time difference (5 minutes = 300 seconds)
  try {
    const prevTime = new Date(prevMessage.timestamp).getTime();
    const currentTime = new Date(currentMessage.timestamp).getTime();
    const diffInSeconds = (currentTime - prevTime) / 1000;
    return diffInSeconds < 300; // 5 minutes
  } catch (error) {
    return false;
  }
};
